import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, FileText, Pencil, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { userService } from "../../../_services";

function VideoCard({ videoData, moduleId, showVideoModal, getVideoId: getVideoIdProp, editVideo, videoDeleted }) {
    const [videoid, setVideoid] = useState('');
    const [deletePopup, setDeletePopup] = useState(false);
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [deleteVideoLoader, setDeleteVideoLoader] = useState(false);
    const [deleteSuccessMsg, setDeleteSuccessMsg] = useState('');
    const [isPdfAvailable, setIsPdfAvailable] = useState(false);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        setUserId(Usermeta_Data.split(':')[0]);
        setUserName(Usermeta_Data.split(':')[1]);

        if (videoData?.mediaLink && videoData.mediaLink !== '') {
            const id = videoData.mediaLink.split('v=')[1];
            setVideoid(id);
        }

        if (videoData?.mediaType === 'application/pdf' && videoData.mediaBase64) {
            fetch(videoData.mediaBase64, { method: 'HEAD' })
                .then(res => { if (res.ok) setIsPdfAvailable(true); })
                .catch(() => setIsPdfAvailable(false));
        }
    }, [videoData]);

    const handleVideoPlay = () => {
        const videotitle = videoData ? videoData.mediaTitle : '';
        showVideoModal();
        getVideoIdProp(videoid, videotitle);
    };

    const handleImageOpen = (base64Data) => {
        window.open(window.location.origin + base64Data, '_blank');
    };

    const handleEditVideo = (e) => {
        e.stopPropagation();
        editVideo();
    };

    const openDeletePop = (e) => {
        e.stopPropagation();
        setDeletePopup(true);
        setDeleteSuccessMsg('');
    };

    const closeDeletePop = () => setDeletePopup(false);

    const handleDeleteVideo = () => {
        setDeleteVideoLoader(true);
        try {
            userService.DeleteMedia(
                userId,
                userName,
                moduleId,
                videoData.mediaTitle,
                videoData.mediaLink,
                videoData.mediaId
            ).then(response => {
                setDeleteVideoLoader(false);
                if (response.length > 0 && response[0].code === '1') {
                    setDeleteSuccessMsg(response[0].message);
                    videoDeleted();
                    setTimeout(() => closeDeletePop(), 1500);
                }
            });
        } catch (err) {
            setDeleteVideoLoader(false);
        }
    };

    if (!videoData) return null;

    const isVideo = videoData?.mediaLink && !videoData?.mediaBase64;
    const isPdf = videoData?.mediaType === 'application/pdf';
    const isImage = videoData?.mediaBase64 && !isPdf;

    return (
        <>
            <Card className="group overflow-hidden transition-all hover:shadow-lg">
                <div
                    className="relative aspect-video bg-gray-100 cursor-pointer"
                    onClick={() => videoData?.mediaBase64 ? handleImageOpen(videoData.mediaBase64) : handleVideoPlay()}
                >
                    {isVideo ? (
                        <>
                            <img
                                src={`https://img.youtube.com/vi/${videoid}/0.jpg`}
                                className="w-full h-full object-cover"
                                alt={videoData.mediaTitle}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                                <div className="bg-accent rounded-full p-3 group-hover:scale-110 transition-transform">
                                    <Play className="h-6 w-6 text-white fill-white" />
                                </div>
                            </div>
                        </>
                    ) : isPdf ? (
                        <>
                            {isPdfAvailable ? (
                                <iframe
                                    src={videoData.mediaBase64}
                                    className="w-full h-full pointer-events-none"
                                    title={videoData.mediaTitle}
                                />
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-sm text-muted-foreground font-medium">PDF file not found</p>
                                </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none">
                                <div className="bg-accent rounded-full p-3 group-hover:scale-110 transition-transform">
                                    <FileText className="h-6 w-6 text-white" />
                                </div>
                            </div>
                        </>
                    ) : isImage ? (
                        <>
                            <img
                                src={videoData.mediaBase64}
                                className="w-full h-full object-cover"
                                alt={videoData.mediaTitle}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                                <div className="bg-accent rounded-full p-3 group-hover:scale-110 transition-transform">
                                    <ExternalLink className="h-6 w-6 text-white" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-full bg-gray-50">
                            <FileText className="h-16 w-16 text-muted-foreground/70" />
                        </div>
                    )}

                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 bg-background hover:bg-muted text-primary"
                            onClick={handleEditVideo}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="destructive"
                            size="icon"
                            className="h-8 w-8"
                            onClick={openDeletePop}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <CardContent className="p-3">
                    <h4 className="text-md font-bold text-primary truncate" title={videoData.mediaTitle}>
                        {videoData.mediaTitle}
                    </h4>
                </CardContent>
            </Card>

            <Dialog open={deletePopup} onOpenChange={closeDeletePop}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Video</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this video?
                        </DialogDescription>
                    </DialogHeader>
                    {deleteSuccessMsg && (
                        <p className="text-sm text-green-600">{deleteSuccessMsg}</p>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDeletePop} disabled={deleteVideoLoader}>
                            No
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteVideo} disabled={deleteVideoLoader}>
                            {deleteVideoLoader ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Deleting</> : 'Yes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default VideoCard;

